import { skipToken, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  getV1CompaniesAccountsTypeahead,
  GetV1CompaniesAccountsTypeaheadData,
} from '@treadinc/horizon-api-spec';
import { uniqBy } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';

import { extractPagination } from '~services/pagination';
import {
  ActingCompany,
  CONNECTED_VENDOR_ACCOUNTS_QUERY,
} from '~utils/vendorCompanyOptions';

import {
  InfiniteQuery,
  InfiniteQueryFetchReason,
  InfiniteQueryParamsState,
} from './constants';
import { buildEquipmentOwnerOptions } from './equipmentOwnerOptions';
import { deriveInfiniteQueryBaseParams } from './utils';

export default function useEquipmentOwnersInfiniteQuery(
  queryKey: string,
  companyId: string,
  actingCompany: ActingCompany,
): InfiniteQuery {
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<InfiniteQueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId, actingCompany.id],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? async ({ pageParam, signal }) => {
          const query: GetV1CompaniesAccountsTypeaheadData['query'] = {
            ...deriveInfiniteQueryBaseParams({ ...queryParams, ...pageParam }),
            ...CONNECTED_VENDOR_ACCOUNTS_QUERY,
          };

          const response = await getV1CompaniesAccountsTypeahead({
            path: { 'company-id': companyId },
            query,
            signal,
          });

          const options = buildEquipmentOwnerOptions(
            response.data.data,
            actingCompany,
            queryParams.filters.query,
            !pageParam?.after,
          );
          const pagination = extractPagination(response);

          setQueryParams((state) => ({
            ...state,
            pagination: { ...pagination, hasMore: Boolean(pagination.after) },
          }));

          return options;
        }
      : skipToken,
  });

  // Two vendor accounts can resolve to the same company across pages
  const allData = useMemo(
    () => uniqBy((data?.pages ?? []).flat(), (option) => option.value),
    [data?.pages],
  );

  const fetchReason = useMemo(() => {
    if (isFetchingNextPage) {
      return InfiniteQueryFetchReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return InfiniteQueryFetchReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, [queryKey]);

  return { clear, data: allData, fetch, fetchMore, fetchReason, isDummyQuery: false };
}
